import {
    BackendQueryFormat,
    BackendQueryRule,
    Operator,
    OperatorValue,
    QueryField,
    QueryGroup,
    QueryRule,
    QuerySource,
    SavedQueryAPI,
    SavedQueryConditionRule,
    SavedQueryWithConditions,
} from '../types/QueryBuilder';

export const SIMPLE_QUERY_CATEGORY = 'SIMPLE_QUERY';

const SIMPLE_QUERY_EXTRA_FIELD_NAMES = new Set(['CLASSIFICATION_PRODUCT']);

export const getSimpleQueryFields = (fields: QueryField[]): QueryField[] =>
    fields.filter(field => field.Categorie === SIMPLE_QUERY_CATEGORY || SIMPLE_QUERY_EXTRA_FIELD_NAMES.has(field.name));

const ADVANCED_SOURCE_VALUES = new Set(['ADVANCED', 'DATAQUERYSERVICE']);

export const isAdvancedQuerySource = (value: unknown): boolean =>
    ADVANCED_SOURCE_VALUES.has(String(value ?? '').trim().toUpperCase());

export const toQuerySource = (value: unknown): QuerySource =>
    isAdvancedQuerySource(value) ? 'ADVANCED' : 'SIMPLE';

const operator = (value: OperatorValue, labelKey: string): Operator => ({ value, labelKey });

const EQUALITY_OPERATORS = [operator('=', 'equals'), operator('!=', 'notEquals')];

const NUMBER_OPERATORS = [
    ...EQUALITY_OPERATORS,
    operator('>', 'greaterThan'),
    operator('<', 'lessThan'),
    operator('>=', 'greaterOrEqual'),
    operator('<=', 'lessOrEqual'),
    operator('between', 'between'),
];

const DATE_OPERATORS = [
    ...EQUALITY_OPERATORS,
    operator('<', 'before'),
    operator('>', 'after'),
    operator('between', 'between'),
];

const LIST_OPERATORS = [...EQUALITY_OPERATORS, operator('in', 'in'), operator('not in', 'notIn')];

const TEXT_OPERATORS = [...EQUALITY_OPERATORS, operator('contains', 'contains'), operator('not contains', 'notContains')];

const LIST_EDITOR_TYPES = new Set(['select', 'multiselect', 'checkbox']);

export const getOperatorsForField = (field: QueryField | undefined): Operator[] => {
    if (!field) return EQUALITY_OPERATORS;
    if (field.datatype === 'NUMBER' || field.inputType === 'number') return NUMBER_OPERATORS;
    if (field.datatype === 'DATERANGE' || field.inputType === 'date') return DATE_OPERATORS;
    if (field.valueEditorType && LIST_EDITOR_TYPES.has(field.valueEditorType)) return LIST_OPERATORS;
    return TEXT_OPERATORS;
};

export const getFieldsByCategory = (fields: QueryField[], category: string): QueryField[] =>
    fields.filter(field => field.Categorie === category);

export const generateId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export const createEmptyGroup = (): QueryGroup => ({ id: generateId(), combinator: 'AND', rules: [] });

const isListOperator = (value: OperatorValue | undefined): value is 'in' | 'not in' =>
    value === 'in' || value === 'not in';

const hasValue = (value: QueryRule['value'] | QueryRule['valueTo'] | QueryRule['notValue']): boolean => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some(item => String(item).trim() !== '');
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

export const isRuleValid = (rule: QueryRule): boolean => {
    if (!rule.field || !rule.operator) return false;
    if (!hasValue(rule.value) && !hasValue(rule.notValue)) return false;
    if (rule.operator !== 'between') return true;
    if (typeof rule.value === 'string' && rule.value.includes('|')) {
        const [min, max] = rule.value.split('|').map(bound => bound.trim() !== '');
        return rule.field === 'CUSTOMER_ACCOUNT' ? min || max : min && max;
    }
    return hasValue(rule.valueTo);
};

export const isGroupValid = (group: QueryGroup): boolean =>
    group.rules.length > 0 && group.rules.every(isRuleValid);

export const hasAnyRule = (groups: QueryGroup[]): boolean =>
    groups.some(group => group.rules.length > 0);

const toBackendList = (value: QueryRule['value']): string[] => Array.isArray(value)
    ? value.map(item => String(item).trim()).filter(Boolean)
    : String(value ?? '').split(',').map(item => item.trim()).filter(Boolean);

const toBackendRules = (rule: QueryRule): BackendQueryRule[] => {
    const field = rule.field || '';

    if (isListOperator(rule.operator)) {
        const rules: BackendQueryRule[] = [];
        const values = toBackendList(rule.value);
        if (values.length > 0) rules.push({ field, operator: rule.operator, value: values });
        if (hasValue(rule.notValue)) {
            rules.push({ field, operator: rule.operator === 'in' ? 'not in' : 'in', value: toBackendList(rule.notValue) });
        }
        return rules.length > 0 ? rules : [{ field, operator: rule.operator, value: [] }];
    }

    let value = Array.isArray(rule.value)
        ? rule.value.map(item => String(item).trim()).filter(Boolean).join(',')
        : rule.value === null || rule.value === undefined ? '' : String(rule.value);
    let valueTo: string | undefined;

    if (rule.operator === 'between' && value.includes('|')) {
        [value = '', valueTo = ''] = value.split('|');
    } else if (rule.operator === 'between' && rule.valueTo) {
        valueTo = String(rule.valueTo);
    }

    return [{ field, operator: rule.operator || '=', value, ...(valueTo !== undefined && { valueTo }) }];
};

export const convertGroupsToBackendFormat = (groups: QueryGroup[]): BackendQueryFormat => {
    const validGroups = groups
        .map(group => group.rules.filter(isRuleValid))
        .filter(rules => rules.length > 0);

    if (validGroups.length === 0) return { combinator: 'and', rules: [] };
    if (validGroups.length === 1) return { combinator: 'and', rules: validGroups[0].flatMap(toBackendRules) };
    return { combinator: 'or', rules: validGroups.map(rules => ({ combinator: 'and', rules: rules.flatMap(toBackendRules) })) };
};

const toGroup = (rules: QueryRule[]): QueryGroup => ({ id: generateId(), combinator: 'AND', rules });

const toRuleValue = (rule: SavedQueryConditionRule): QueryRule['value'] => {
    const { value } = rule;
    if (!isListOperator(rule.operator) || value === undefined || value === null || Array.isArray(value)) return value;
    if (typeof value === 'string' && value.includes(',')) return value.split(',').map(item => item.trim());
    return [String(value)];
};

const toQueryRule = (rule: SavedQueryConditionRule): QueryRule => ({
    id: generateId(),
    field: rule.field,
    operator: rule.operator,
    value: toRuleValue(rule),
    ...(rule.valueTo && { valueTo: rule.valueTo }),
});

const mergeDualListRules = (rules: QueryRule[]): QueryRule[] => {
    const merged: QueryRule[] = [];
    const indexByField = new Map<string, number>();

    rules.forEach(rule => {
        const pairKey = rule.field && isListOperator(rule.operator) ? rule.field : undefined;
        const existingIndex = pairKey ? indexByField.get(pairKey) : undefined;

        if (existingIndex === undefined) {
            merged.push(rule);
            if (pairKey) indexByField.set(pairKey, merged.length - 1);
            return;
        }

        const existing = merged[existingIndex];
        const existingValues = Array.isArray(existing.value) ? existing.value.map(String) : [];
        const incomingValues = Array.isArray(rule.value) ? rule.value.map(String) : [];
        merged[existingIndex] = existing.operator === 'in'
            ? { ...existing, value: existingValues, notValue: incomingValues }
            : { ...existing, operator: 'in', value: incomingValues, notValue: existingValues };
    });

    return merged;
};

const toMergedGroup = (rules: SavedQueryConditionRule[]): QueryGroup =>
    toGroup(mergeDualListRules(rules.map(toQueryRule)));

const toSameFieldListGroup = (
    rules: SavedQueryConditionRule[],
    sourceOperator: OperatorValue,
    listOperator: 'in' | 'not in',
): QueryGroup[] | null => {
    const [first] = rules;
    const isSameFieldList = rules.length > 1
        && rules.every(rule => rule.field && rule.operator === sourceOperator && rule.field === first.field);
    if (!isSameFieldList) return null;
    return [toGroup([{ id: generateId(), field: first.field, operator: listOperator, value: rules.map(rule => String(rule.value)) }])];
};

const parseConditions = (conditions: SavedQueryWithConditions['conditions']): SavedQueryConditionRule | undefined => {
    if (typeof conditions !== 'string') return conditions?.[0];
    try {
        const parsed: unknown = JSON.parse(conditions);
        return Array.isArray(parsed) ? parsed[0] : undefined;
    } catch {
        return undefined;
    }
};

export const convertSavedQueryToGroups = (savedQuery: SavedQueryAPI | SavedQueryWithConditions): QueryGroup[] => {
    const root = parseConditions(savedQuery?.conditions);
    const rules = root?.rules;
    if (!rules) return [createEmptyGroup()];

    if (root.combinator === 'or') {
        return toSameFieldListGroup(rules, '=', 'in') ?? rules.map(group => toMergedGroup(group.rules ?? []));
    }

    if (root.combinator === 'and') {
        const [first] = rules;
        const sameFieldList = toSameFieldListGroup(rules, '!=', 'not in');
        if (sameFieldList) return sameFieldList;
        if (first?.combinator === 'or' && first.rules) return first.rules.map(group => toMergedGroup(group.rules ?? []));
        return [toMergedGroup(rules)];
    }

    const [main] = rules;
    return main?.field && main.operator ? [toGroup([toQueryRule(main)])] : [createEmptyGroup()];
};
